import {Chip, Input} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {createFileRoute} from '@tanstack/react-router'
import {useState} from "react";
import {apiActionImproved} from "../../../api";
import PButton from "../../../components/PButton.tsx";
import Header from "../../../Header.tsx";

export const Route = createFileRoute('/_app/master/pricing-lab')({
    component: RouteComponent,
})

/**
 * Master-only controls for walking the new pricing model on staging.
 *
 * None of the pricing work is reachable without a database change — every club is legacy and the
 * pricing model has no UI anywhere else — so this is the way in. Everything here is manual and
 * reversible: every action has an inverse on this screen, and nothing runs on its own.
 *
 * The entitlements master switch is NOT here: it already exists on /master/activation under
 * "Platform switches", alongside the other two.
 */
function RouteComponent() {
    const [clubUid, setClubUid] = useState('');
    // Find the club by its organizer instead: admins granting a plan know the email, not the uid.
    const [organizer, setOrganizer] = useState('');
    const [state, setState] = useState<any>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [output, setOutput] = useState<string | null>(null);
    // Processing fee override inputs — empty string = keep the schedule value for that part.
    const [feeRate, setFeeRate] = useState('');
    const [feeFixed, setFeeFixed] = useState('');
    // Give a plan: how long for. The only tier action that works on production.
    const [giftDays, setGiftDays] = useState('30');
    const [giftNote, setGiftNote] = useState('');
    // Which command produced the output on screen, and whether it actually applied. Kept beside the
    // output rather than inferred from the buttons, so what is labelled is what really ran.
    const [lastCommand, setLastCommand] = useState<string | null>(null);
    const [lastApplied, setLastApplied] = useState(false);
    const [lastChanged, setLastChanged] = useState(false);
    // Set when Load refused because the organizer has no club — offers creating one in place.
    const [noClubFor, setNoClubFor] = useState<string | null>(null);
    // Friendly Wagers: the game the wager buttons act on (it must be this club's).
    const [wagerGame, setWagerGame] = useState('');

    const call = async (action: string, extra: Record<string, unknown> = {}) => {
        setBusy(action);
        try {
            const res: any = await apiActionImproved('master/pricing-lab' as any, {
                action,
                club_uid: clubUid.trim(),
                organizer: organizer.trim(),
                ...extra,
            });
            if (res?.output !== undefined) {
                setOutput(res.output);
                setLastCommand(res.command ?? null);
                setLastApplied(!!res.applied);
                setLastChanged(!!res.changed);
            }
            if (res?.club) {
                setState(res);
                // Found via the organizer field? Pin the club uid so every later button targets
                // exactly the club on screen, even if the organizer field is edited meanwhile.
                if (res.club.uid && res.club.uid !== clubUid.trim()) {
                    setClubUid(res.club.uid);
                }
            }
            if (res?.message) {
                addToast({title: res.message, color: 'success'});
            }
        } catch (e: any) {
            // The one refusal with a next step: a club-less organizer can be given their
            // commercial object right here instead of dead-ending on the error.
            if (typeof e?.message === 'string' && e.message.includes('has no club')) {
                setNoClubFor(organizer.trim());
            }
            // A bare 404 is the lab refusing a testing-only action on production (PricingLabController).
            addToast({title: e?.message || 'Not available on production: this is a testing-only action. Use Give a plan, or staging.', color: 'danger'});
        } finally {
            setBusy(null);
        }
    };

    const c = state?.club;
    const enforced = state?.enforcement?.entitlements_enforced;
    // Scheduler heartbeat: stamped every minute by the Kernel. Stale/absent means schedule:run is
    // not firing on this host — the trial engine only "runs itself" when this is green.
    const heartbeatAt = state?.enforcement?.scheduler_heartbeat_at as string | null | undefined;
    const heartbeatAgeMin = heartbeatAt ? Math.round((Date.now() - new Date(heartbeatAt).getTime()) / 60_000) : null;
    const schedulerAlive = heartbeatAgeMin !== null && heartbeatAgeMin <= 5;

    const Btn = ({action, label, extra, color}: {action: string; label: string; extra?: Record<string, unknown>; color?: any}) => (
        <PButton size="sm" variant="flat" color={color ?? 'default'}
                 isDisabled={!!busy || !clubUid.trim()}
                 onPress={() => call(action, extra)}>
            {busy === action ? '…' : label}
        </PButton>
    );

    return (
        <div className="mx-auto w-full max-w-5xl px-4 pb-12">
            <Header />
            <h1 className="text-2xl font-bold mt-4">Pricing lab</h1>
            <p className="text-sm text-default-500 mb-2">
                Master-only. Every action is manual and reversible. The <strong>entitlements enforced</strong> switch
                lives on <a className="underline" href="/master/activation">/master/activation</a> under Platform switches.
            </p>
            {/* The reserved fictional range never reaches Twilio off-production, so OTP flows
                (claims, phone login) are testable without burning real numbers — and since the
                claim flow refuses duplicate numbers, the RANGE gives a fresh "player" every time. */}
            <div className="rounded-lg border border-default-200 bg-default-50 px-3 py-2 text-xs text-default-600 mb-4">
                <strong>Test phones (staging/local only):</strong> any number from{' '}
                <code>+44 7700 900000</code> to <code>+44 7700 900999</code> skips the SMS and verifies with
                code <code>000000</code>. Use a different number per player — duplicates are refused by design.
            </div>

            <div className="flex flex-wrap gap-2 items-end mb-4">
                <Input label="Club UID" value={clubUid} onValueChange={setClubUid} className="max-w-xs" size="sm" />
                <span className="pb-2 text-xs text-default-400">or</span>
                <Input label="Organizer email or UID" value={organizer} onValueChange={setOrganizer}
                       className="max-w-xs" size="sm"
                       description="Finds their club — the UID fills in on load" />
                <PButton size="sm" color="primary" isDisabled={(!clubUid.trim() && !organizer.trim()) || !!busy}
                         onPress={() => call('state')}>Load</PButton>
            </div>

            {/* A club-less organizer is permanently ungated (the entitlement gate falls to the
                legacy organizers-are-open path), so this is the only route onto the commercial
                track for them. Creating grants nothing — the trial engine takes over from here. */}
            {!c && noClubFor && (
                <div className="mb-4 rounded-lg border border-warning-300 bg-warning-50 px-4 py-3 text-sm dark:bg-warning-900/20">
                    <p className="font-medium">{noClubFor} has no club or community, so there is nothing to put a plan on.</p>
                    <p className="mt-1 text-default-600">
                        Create a community for them and they join the commercial track: their next scored game starts
                        the 7-day trial. No tier or trial is granted by this button itself.
                    </p>
                    <PButton size="sm" color="warning" className="mt-2" isDisabled={!!busy}
                             onPress={async () => {
                                 setNoClubFor(null);
                                 await call('create_club');
                             }}>
                        {busy === 'create_club' ? '…' : 'Create their community'}
                    </PButton>
                </div>
            )}

            {!c ? (
                busy === 'state' || busy === 'create_club' ? <Spinner size="sm" /> :
                    !noClubFor && <p className="text-sm text-default-500">Enter a club UID or an organizer email and press Load.</p>
            ) : (
                <>
                    {/* ---- one screen showing what you are looking at ---- */}
                    <div className="rounded-xl border border-default-200 bg-content1 p-4 mb-4">
                        <div className="flex flex-wrap gap-2 mb-3">
                            <Chip size="sm" color={c.is_legacy ? 'default' : 'primary'}>
                                {c.pricing_model_label ?? 'unknown'} (model {c.pricing_model})
                            </Chip>
                            <Chip size="sm" color={enforced ? 'warning' : 'default'}>
                                enforcement {enforced ? 'ON' : 'OFF'}
                            </Chip>
                            <Chip size="sm" color={c.one_off_enforced ? 'warning' : 'default'}
                                  title="Organizer path. One-off pays inside the first event (sized pass at go-live, no trial); community is the trial + subscription path. 'enforced' needs both the entitlements switch and the one-off switch on.">
                                path {c.organizer_path ?? 'community'}{c.one_off_enforced ? ' (enforced)' : ''}
                            </Chip>
                            <Chip size="sm" color={schedulerAlive ? 'success' : 'danger'}
                                  title="The trial engine (warn / expire / autostart / dunning) runs itself on the scheduler — daily in production, every 15 min elsewhere. Red means schedule:run is not firing on this server.">
                                scheduler {schedulerAlive ? `alive (${heartbeatAgeMin}m ago)` : heartbeatAt ? `STALE (${heartbeatAgeMin}m ago)` : 'NO HEARTBEAT'}
                            </Chip>
                            <Chip size="sm">effective tier: {c.effective_tier_label}</Chip>
                            {c.trial_tier && <Chip size="sm" color="secondary">trial active</Chip>}
                            {c.grace_until && <Chip size="sm" color="danger">in grace</Chip>}
                        </div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
                            {[
                                ['Club', `${c.name ?? '—'} (${c.uid})`],
                                ['Subscription tier', c.subscription_tier ?? '—'],
                                ['Status', c.subscription_status ?? '—'],
                                ['Trial started', c.trial_started_at ?? '—'],
                                ['Trial expires', c.trial_expires_at ?? '—'],
                                ['Trial warned', c.trial_warned_at ?? '—'],
                                ['Trial v3 rule', c.trial_v3 ? `${c.trial_v3.rule}${c.trial_v3.enabled ? '' : ' (switch off)'}${c.trial_v3.applies ? ' · in force' : ''}` : '—'],
                                ['Trial v3 count', c.trial_v3 ? (c.trial_v3.rule === 'league'
                                    ? `${c.trial_v3.matchdays_completed} / ${c.trial_v3.matchdays_total} matchdays (cap ${c.trial_v3.days_cap}d from the first)`
                                    : `${c.trial_v3.trial_hours}h · ${c.trial_v3.hours_left}h left`) : '—'],
                                ['Trial v3 computed expiry', c.trial_v3?.computed_expires_at ?? '—'],
                                ['Grace until', c.grace_until ?? '—'],
                                ['Dunning stage', c.dunning_stage ?? '—'],
                                ['Last downgrade', c.last_downgrade ?? '—'],
                                ['Gift until', c.gift_until ? `${c.gift_until}${c.gift?.note ? ` (${c.gift.note})` : ''}` : '—'],
                            ].map(([k, v]) => (
                                <div key={k as string}>
                                    <div className="text-xs text-default-500">{k}</div>
                                    <div className="font-medium break-all">{String(v)}</div>
                                </div>
                            ))}
                        </div>
                        {state.owner && (
                            <div className="mt-3 text-xs text-default-500">
                                Organizer: {state.owner.email} · trial grant on file: {state.owner.trial_grant ? 'yes' : 'no'}
                            </div>
                        )}
                    </div>

                    {/* ---- pricing model ---- */}
                    <Section title="Pricing model">
                        <Btn action="set_pricing_model" label="Set to CURRENT (2)" extra={{value: 2}} color="primary" />
                        <Btn action="set_pricing_model" label="Set back to LEGACY (1)" extra={{value: 1}} />
                    </Section>

                    {/* ---- organizer path ----
                        Model-2 only. One-off: no pre-trial, no trial, a sized Event Pass at go-live
                        (draw or first score) for tournaments beyond the free band. The switch that
                        enforces it ("One-off organizer path") lives on /master/activation. */}
                    <Section title="Organizer path">
                        <div className="w-full text-xs text-default-500 mb-1">
                            Currently <strong>{c.organizer_path ?? 'community'}</strong>
                            {' '}· one-off switch {state?.enforcement?.one_off_path_enabled ? 'ON' : 'OFF'}
                            {' '}· enforced for this club: {c.one_off_enforced ? 'yes' : 'no'}
                        </div>
                        <Btn action="set_organizer_path" label="Set to ONE-OFF" extra={{value: 'one_off'}} color="primary" />
                        <Btn action="set_organizer_path" label="Set to COMMUNITY" extra={{value: 'community'}} />
                    </Section>

                    {/* ---- trial ---- */}
                    <Section title="Trial">
                        <Btn action="start_trial" label="Start trial now" color="primary" />
                        {/* The last follow-up email's offer: reply → a free week. Works on production. */}
                        <Btn action="extend_trial" label="Give 7 more days" color="success" />
                        <Btn action="recompute_trial" label="Recompute (v3, dry run)" extra={{apply: false}} />
                        <Btn action="recompute_trial" label="Recompute (v3) — apply" extra={{apply: true}} color="secondary" />
                        <Btn action="expire_trial" label="Fast-forward expiry to the past" color="warning" />
                        <Btn action="clear_trial" label="Clear trial" />
                    </Section>

                    {/* ---- test-only resets ----
                        Separated and labelled, because these two deliberately break rules that hold
                        everywhere else: the once-per-mailbox trial grant is permanent in production,
                        and nothing there returns a club to "new". They exist so a flow can be walked
                        more than once without registering a fresh email each time. */}
                    <Section title="Reset (testing only)">
                        <div className="w-full text-xs text-default-500 mb-1">
                            These clear permanent records that production never clears. Events, games, players and
                            passes are left alone — only the club&rsquo;s billing state is reset.
                        </div>
                        <Btn action="reset_trial" label="Reset trial (testing only)" color="danger" />
                        <Btn action="reset_whatsapp_trial" label="Forget free WhatsApp session (testing only)" color="danger" />
                        <Btn action="reset_club" label="Reset club to new (testing only)" color="danger" />
                    </Section>

                    {/* ---- subscription / dunning ----
                        The plan buttons land through SubscriptionDowngradeService::reactivate — the
                        same handler a real payment uses — so the club gets the tier, Active status,
                        cleared trial columns and unfrozen leagues, exactly as paying would. Free is
                        here too so a plan granted by hand can be taken back from the same row. */}
                    {/* ---- give a plan ----
                        A tier for a term with no Stripe behind it, ended by the daily
                        subscriptions:gifts-expire pass. Works on production: a free month for a
                        friend or a partner, taken back early with End gift. */}
                    <Section title="Give a plan (works on production)">
                        <div className="flex w-full flex-wrap items-end gap-2">
                            <Input label="Days" value={giftDays} onValueChange={setGiftDays} size="sm" className="max-w-28" type="number" />
                            <Input label="Note (who / why)" value={giftNote} onValueChange={setGiftNote} size="sm" className="max-w-72" />
                            {([['Starter', 150], ['Plus', 200], ['Pro', 300]] as [string, number][]).map(([label, value]) => (
                                <PButton key={value} size="sm" color="primary" isDisabled={!!busy || !clubUid.trim() || !(Number(giftDays) > 0)}
                                         onPress={() => call('gift_plan', {value, days: Number(giftDays), note: giftNote})}>
                                    {busy === 'gift_plan' ? '…' : `Give ${label}`}
                                </PButton>
                            ))}
                            <Btn action="end_gift" label="End gift now" color="danger" />
                        </div>
                        <p className="text-xs text-default-500">
                            The club gets the plan exactly as if it had paid, until the date shown under Gift until; then it goes back to Free the way a lapsed subscription does.
                        </p>
                    </Section>

                    <Section title="Set plan (as a successful payment would)">
                        <Btn action="set_tier" label="Starter ($19)" extra={{value: 150}} color="primary" />
                        <Btn action="set_tier" label="Plus ($39)" extra={{value: 200}} color="primary" />
                        <Btn action="set_tier" label="Pro ($129)" extra={{value: 300}} color="primary" />
                        <Btn action="set_tier" label="Back to Free (Pay-as-you-go)" extra={{value: 100}} />
                    </Section>

                    <Section title="Dunning">
                        <Btn action="open_grace" label="Simulate failed payment" color="warning" />
                        <Btn action="resolve_grace" label="Simulate successful payment" color="success" />
                    </Section>

                    {/* ---- processing fee override ----
                        A special % and/or fixed per-player amount for THIS organizer, replacing the
                        country schedule everywhere fees are computed and shown. Works on production
                        too (unlike the trial/tier actions) — granting a partner a special rate is a
                        real production task. */}
                    {state.fees && (
                        <Section title="Processing fee override">
                            <div className="w-full text-xs text-default-500 mb-1">
                                Standard for this organizer ({state.fees.country}, current plan):{' '}
                                <strong>{state.fees.schedule_rate}% + {(state.fees.schedule_fixed_cents / 100).toFixed(2)} {state.fees.currency}</strong> per player.
                                {' '}Currently charging:{' '}
                                <strong>
                                    {state.fees.effective_rate}% + {(state.fees.effective_fixed_cents / 100).toFixed(2)} {state.fees.currency}
                                </strong>
                                {(state.fees.override_rate != null || state.fees.override_fixed_cents != null) && (
                                    <span className="ml-1 rounded bg-warning-100 px-1.5 py-0.5 font-semibold text-warning-800">OVERRIDE ACTIVE</span>
                                )}
                            </div>
                            <div className="flex w-full flex-wrap items-end gap-2">
                                <Input label={`Rate % (blank = schedule ${state.fees.schedule_rate}%)`} value={feeRate}
                                       onValueChange={setFeeRate} size="sm" className="max-w-48" type="number" />
                                <Input label={`Fixed (${state.fees.currency}, blank = schedule ${(state.fees.schedule_fixed_cents / 100).toFixed(2)})`}
                                       value={feeFixed} onValueChange={setFeeFixed} size="sm" className="max-w-56" type="number" />
                                <PButton size="sm" color="primary" isDisabled={!!busy || (!feeRate.trim() && !feeFixed.trim())}
                                         onPress={() => call('set_fees', {
                                             rate: feeRate.trim() === '' ? null : Number(feeRate),
                                             fixed_cents: feeFixed.trim() === '' ? null : Math.round(Number(feeFixed) * 100),
                                         })}>
                                    {busy === 'set_fees' ? '…' : 'Apply override'}
                                </PButton>
                                <PButton size="sm" variant="flat" color="danger" isDisabled={!!busy}
                                         onPress={() => { setFeeRate(''); setFeeFixed(''); call('set_fees', {rate: null, fixed_cents: null}); }}>
                                    Clear (back to schedule)
                                </PButton>
                            </div>
                        </Section>
                    )}

                    {/* ---- leagues ---- */}
                    <Section title="Leagues">
                        <Btn action="freeze_leagues" label="Freeze running leagues" color="warning" />
                        <Btn action="unfreeze_leagues" label="Unfreeze all" color="success" />
                    </Section>

                    {/* ---- trial lifecycle emails ---- */}
                    <Section title="Trial emails">
                        <div className="w-full text-xs text-default-500 mb-1">
                            Sends the REAL email to <strong>the organizer's own address</strong> — exactly what the
                            scheduler would send. Aim carefully on production.
                        </div>
                        {([['warn', 'T-3 warning'], ['ended', 'Day 0 · ended'], ['after_3', '+3 days'],
                           ['after_7', '+7 days'], ['after_14', '+14 days · last']] as const).map(([stage, label]) => (
                            <PButton key={stage} size="sm" variant="flat" isDisabled={!!busy}
                                     onPress={() => call('send_trial_email', {value: stage})}>
                                {busy === 'send_trial_email' ? '…' : label}
                            </PButton>
                        ))}
                    </Section>

                    {state.leagues?.length > 0 && (
                        <div className="rounded-xl border border-default-200 bg-content1 p-4 mb-4 overflow-x-auto">
                            <div className="text-sm font-bold mb-2">Leagues</div>
                            <table className="min-w-full text-sm">
                                <thead className="text-xs text-default-500">
                                    <tr><th className="text-left pr-4">uid</th><th className="text-left pr-4">title</th>
                                        <th className="text-left pr-4">frozen</th><th className="text-left pr-4">scored</th>
                                        <th className="text-left pr-4">claimed / needed</th><th className="text-left">unlocked</th></tr>
                                </thead>
                                <tbody>
                                    {state.leagues.map((l: any) => (
                                        <tr key={l.uid} className="border-t border-default-100">
                                            <td className="pr-4 font-mono text-xs">{l.uid}</td>
                                            <td className="pr-4">{l.title}</td>
                                            <td className="pr-4">{l.frozen_at ? 'yes' : 'no'}</td>
                                            <td className="pr-4">{l.has_scored_game ? 'yes' : 'no'}</td>
                                            <td className="pr-4">{l.claimed} / {l.required} of {l.entrants}{l.path_a_met ? ' ✓' : ''}</td>
                                            <td>{l.unlocked ? 'yes' : 'no'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ---- Friendly Wagers: a club's markets by hand (testing only) ----
                        The game must be this club's (the server checks it with the one club
                        resolver). Settle takes the result to settle on, as a finished game would:
                        a draw on a two-way market refunds; none is no result, every tip refunded. */}
                    {state.friendly_wagers && (() => {
                        const fw = state.friendly_wagers;
                        const on = (v: boolean) => <span className={v ? 'text-success-600' : 'text-danger-600'}>{v ? 'yes' : 'no'}</span>;
                        const g = {game_uid: wagerGame.trim()};
                        const noGame = !wagerGame.trim();
                        const WBtn = ({action, label, extra, color}: {action: string; label: string; extra?: Record<string, unknown>; color?: any}) => (
                            <PButton size="sm" variant="flat" color={color ?? 'default'} isDisabled={!!busy || !clubUid.trim() || noGame}
                                     onPress={() => call(action, {...g, ...extra})}>
                                {busy === action ? '…' : label}
                            </PButton>
                        );
                        return (
                            <Section title="Friendly Wagers (testing only)">
                                <div className="w-full text-xs text-default-600 mb-1 flex flex-wrap gap-x-4 gap-y-1">
                                    <span>switch {on(fw.switch_on)}</span>
                                    <span>plan allows {on(fw.plan_allows)}</span>
                                    <span>club switched on {on(fw.club_on)}</span>
                                    <span>odds {fw.settings?.odds_mode} · draw {fw.settings?.draw_rule} · start {fw.settings?.starting_balance} · qualify {fw.settings?.min_bets_to_qualify}{fw.settings?.topup_enabled ? ` · top-up to ${fw.settings?.topup_floor}` : ''}</span>
                                    <span>{fw.season ? `${fw.season.name}: ${fw.season.accounts} account(s), ${fw.season.coins} coins` : 'no season yet'}</span>
                                </div>
                                <div className="flex w-full flex-wrap items-end gap-2">
                                    <Input label="Game UID" value={wagerGame} onValueChange={setWagerGame} className="max-w-xs" size="sm" />
                                    <WBtn action="wagers_create_market" label="Create market" color="primary" />
                                    <WBtn action="wagers_force_close" label="Force close" color="warning" />
                                    {(['t1', 't2', 'draw', 'none'] as const).map((r) => (
                                        <PButton key={r} size="sm" variant="flat" isDisabled={!!busy || !clubUid.trim() || noGame}
                                                 onPress={() => call('wagers_settle', {...g, result: r})}>
                                            {busy === 'wagers_settle' ? '…' : `Settle ${r}`}
                                        </PButton>
                                    ))}
                                    <Btn action="wagers_new_season" label="Start new season" color="danger" />
                                </div>
                                {fw.markets?.length > 0 && (
                                    <div className="w-full overflow-x-auto mt-2">
                                        <table className="min-w-full text-sm">
                                            <thead className="text-xs text-default-500">
                                                <tr><th className="text-left pr-4">#</th><th className="text-left pr-4">game</th><th className="text-left pr-4">status</th>
                                                    <th className="text-left pr-4">odds</th><th className="text-left pr-4">closes</th><th className="text-left pr-4">result</th>
                                                    <th className="text-left pr-4">tips · coins</th><th className="text-left">v</th></tr>
                                            </thead>
                                            <tbody>
                                                {fw.markets.map((m: any) => (
                                                    <tr key={m.id} className="border-t border-default-100 cursor-pointer hover:bg-default-50" onClick={() => setWagerGame(m.game_uid)}>
                                                        <td className="pr-4">{m.id}</td>
                                                        <td className="pr-4 font-mono text-xs">{m.game_uid}</td>
                                                        <td className="pr-4">{m.status}{m.void_reason ? ` (${m.void_reason})` : ''}</td>
                                                        <td className="pr-4 text-xs">{Object.entries(m.odds ?? {}).map(([k, v]) => `${k} ${v}`).join(' · ')}</td>
                                                        <td className="pr-4 text-xs">{m.closes_at}</td>
                                                        <td className="pr-4">{m.result_key ?? '—'}</td>
                                                        <td className="pr-4">{m.tips} · {m.coins}</td>
                                                        <td>{m.settle_version}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <p className="mt-1 text-xs text-default-500">Click a row to aim the buttons at its game.</p>
                                    </div>
                                )}
                            </Section>
                        );
                    })()}

                    {/* ---- scheduled passes, on demand ----
                        Two buttons per command, never one. These passes take --apply; without it
                        they only report. A single button hid which mode you were in, so a command
                        that was working correctly — declining to act on a club that did not qualify,
                        or deferring a downgrade because an event was in progress — was
                        indistinguishable from a broken one. */}
                    <Section title="Run a scheduled pass now">
                        <div className="flex w-full flex-col gap-2">
                            {['trials:warn', 'trials:warn-v3', 'trials:recompute', 'trials:expire', 'trials:autostart', 'subscriptions:dunning', 'wagers:sweep', 'tip-coins:weekly-topup'].map((cmd) => (
                                <div key={cmd} className="flex items-center gap-2">
                                    <code className="min-w-52 text-xs">{cmd}</code>
                                    <PButton size="sm" variant="flat" isDisabled={!!busy}
                                             onPress={() => call('run_command', {command: cmd, apply: false})}>
                                        Dry run
                                    </PButton>
                                    <PButton size="sm" variant="flat" color="warning" isDisabled={!!busy}
                                             onPress={() => call('run_command', {command: cmd, apply: true})}>
                                        Apply
                                    </PButton>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {output !== null && (
                        <div className="rounded-xl border border-default-200 bg-content1 p-4">
                            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                                <code className="font-semibold">{lastCommand}</code>
                                <span className={`rounded px-2 py-0.5 font-semibold ${
                                    lastApplied ? 'bg-warning-100 text-warning-800' : 'bg-default-100 text-default-600'
                                }`}>
                                    {lastApplied ? 'APPLIED' : 'DRY RUN — nothing was changed'}
                                </span>
                                {lastApplied && (
                                    <span className={`rounded px-2 py-0.5 font-semibold ${
                                        lastChanged ? 'bg-success-100 text-success-800' : 'bg-default-100 text-default-600'
                                    }`}>
                                        {lastChanged ? 'this club changed' : 'this club did not change'}
                                    </span>
                                )}
                            </div>
                            {/* Full output, verbatim. The commands explain themselves — "0 trial(s)
                                due to end", "deferred_live_event" — and truncating that is what
                                turned a legible outcome into an apparent no-op. */}
                            <pre className="overflow-x-auto whitespace-pre-wrap text-xs">
                                {output.trim() || '(no output)'}
                            </pre>
                            {lastApplied && !lastChanged && (
                                <p className="mt-2 text-xs text-default-500">
                                    The command ran and applied. Nothing moved for this club, which usually means it did
                                    not qualify — a trial that is not due yet, or a downgrade deferred because an event
                                    is still in progress. The output above says which.
                                </p>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Section({title, children}: {title: string; children: React.ReactNode}) {
    return (
        <div className="rounded-xl border border-default-200 bg-content1 p-4 mb-4">
            <div className="text-sm font-bold mb-2">{title}</div>
            <div className="flex flex-wrap gap-2">{children}</div>
        </div>
    );
}
